import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import type { PassengersSchemaType } from "@/shared/schemas/flight/passengersSchema";
import { memo, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import PassengerCard from "./PassengerCard";
import PassengerCardFooter from "./PassengerCardFooter";

interface PassengerCardProps {
  flightDetail: FlightDetail;
  nationality: NationalityRes;
  recentPassengers: RecentPassenger[];
  changeToggle: (value: boolean) => void;
}

const PassengerCards = memo(function PassengerCards({
  flightDetail,
  nationality,
  recentPassengers,
  changeToggle,
}: PassengerCardProps) {
  const { control } = useFormContext<PassengersSchemaType>();
  const { fields } = useFieldArray<PassengersSchemaType>({
    control,
    name: "passengers",
  });

  const [departFlight, returnFlight] = Object.values(flightDetail);
  const hasEnoughSeats = fields.length <= Number(departFlight.capacity);
  const canAddInfant=fields.length === Number(departFlight.capacity);

  const isForeign = returnFlight?.is_foreign || departFlight?.is_foreign;

  const priceDetails: PriceDetails = useMemo(
    () =>
      returnFlight
        ? {
            adult: {
              ...departFlight.price_details.adult,
              payable:
                departFlight.price_details.adult.payable +
                returnFlight.price_details.adult.payable,
            },
            child: {
              ...departFlight.price_details.child,
              payable:
                departFlight.price_details.child.payable +
                returnFlight.price_details.child.payable,
            },
            infant: {
              ...departFlight.price_details.infant,
              payable:
                departFlight.price_details.infant.payable +
                returnFlight.price_details.infant.payable,
            },
          }
        : departFlight.price_details,
    [flightDetail],
  );

  return (
    <Flex className="flex-col w-full gap-y-6">
      <Flex className="w-full! flex-col h-fit py-2 pt-1 px-2 md:px-6 mx-auto">
        <Flex className="w-full flex-col gap-y-10">
          <List
            items={fields}
            renderItems={({ type }, key) => (
              <PassengerCard
                isForeign={isForeign}
                type={type}
                changeToggle={changeToggle}
                nationality={nationality}
                recentPassengers={recentPassengers}
                passengerIndex={key}
                hasEnoughSeats={hasEnoughSeats && fields.length - 1 === key}

              />
            )}
          />
        </Flex>
      </Flex>

      {/* Footer */}

      {hasEnoughSeats && (
        <PassengerCardFooter
          priceDetails={priceDetails}
          isForeign={isForeign}
          canAddInfant={canAddInfant}
        />
      )}
    </Flex>
  );
});

export default PassengerCards;
